import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
} from "recharts";
import { HashLoader } from "react-spinners";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

import { COLORS } from "../../utils/colors.js";
import { ICONS } from "../../utils/icons.js";
import Status from "../../components/TicketStatus.js";
import timeAgo from "../../utils/timeAgo.js";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="label" style={{ color: COLORS.grey }}>
          Tickets sold
        </p>
        <p className="label">{label}</p>
        <p
          className="label"
          style={{ fontSize: "34px" }}
        >{`${payload[0].value}`}</p>
      </div>
    );
  }

  return null;
};

const columns = [
  { label: "Image", id: "image", align: "center", minWidth: 50 },
  { label: "Category", id: "category", align: "center", minWidth: 50 },
  { label: "Status", id: "status", align: "left" },
  { label: "Bought", id: "date", minWidth: 50, align: "center" },
];

export const Overview = ({
  userData,
  event,
  loading,
  dayPeriod,
  numberOfTickets,
  latestTickets,
  ticketSold,
  totalRevenue,
  invalidTickets,
}) => {
  return (
    <div className="quick-summary-container">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "15px",
          margin: "25px 0px",
        }}
      >
        <div className="small-container">
          <div
            className="container-row"
            style={{
              width: "-webkit-fill-container",
              justifyContent: "space-between",
            }}
          >
            <p id="subheader">Total Revenue</p>
            {ICONS.moneyAccent20}
          </div>
          <h2 style={{ fontSize: "34px", margin: "10px 0 0 0" }}>
            {loading ? <HashLoader size={20} /> : totalRevenue + "€"}
          </h2>
        </div>
        <div className="small-container">
          <div
            className="container-row"
            style={{
              width: "-webkit-fill-container",
              justifyContent: "space-between",
            }}
          >
            <p id="subheader">Total tickets created</p>
            {ICONS.ticketAccent20}
          </div>
          <h2 style={{ fontSize: "34px", margin: "10px 0 0 0" }}>
            {loading ? <HashLoader size={20} /> : numberOfTickets}
          </h2>
        </div>
        <div className="small-container">
          <div
            className="container-row"
            style={{
              width: "-webkit-fill-container",
              justifyContent: "space-between",
            }}
          >
            <p id="subheader">Canceled | Refunded | Invalid Tickets</p>
            {ICONS.ticketAccent20}
          </div>
          <h2 style={{ fontSize: "34px", margin: "10px 0 0 0" }}>
            {loading ? (
              <HashLoader size={20} />
            ) : (
              `${invalidTickets.canceled} | ${invalidTickets.refunded} | ${invalidTickets.invalid}`
            )}
          </h2>
        </div>
        <div className="small-container">
          <div
            className="container-row"
            style={{
              width: "-webkit-fill-container",
              justifyContent: "space-between",
            }}
          >
            <p id="subheader">Event Maximum Capacity</p>
            {ICONS.threePeopleAccent20}
          </div>
          <h2 style={{ fontSize: "34px", margin: "10px 0 0 0" }}>
            {event?.capacity}
          </h2>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
        <div
          className="graph-container"
          style={{ width: "60%", height: "470px" }}
        >
          <div style={{ height: "420px" }}>
            <p id="subheader" style={{ padding: "20px" }}>
              Overview
            </p>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                key={Math.random()}
                width={400}
                height={100}
                data={ticketSold}
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 20,
                }}
              >
                <XAxis
                  dataKey="_id"
                  stroke="black"
                  tick={{
                    fontFamily: "AuthenticSans90",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                  domain={[
                    new Date(Date.now() - dayPeriod * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .slice(0, 10),
                    new Date().toISOString().slice(0, 10),
                  ]}
                />
                <YAxis
                  dataKey="tickets_sold"
                  orientation="right"
                  stroke="black"
                  tick={{
                    fontFamily: "AuthenticSans90",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                  domain={ticketSold.length !== 0 ? [0, ticketSold] : [0, 1000]}
                />
                <Tooltip content={<CustomTooltip />} />
                {loading ? (
                  <HashLoader size={20} />
                ) : (
                  <Bar
                    dataKey="tickets_sold"
                    barSize={20}
                    fill={COLORS.mainAccent}
                    label={{
                      value: "Tickets Sold",
                      fontFamily: "AuthenticSans90",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  />
                )}
                <Line type="monotone" dataKey="value" stroke="#8884d8" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <Paper
          sx={{
            width: "40%",
            height: "fit-content",
            border: "1px solid #d8d8d8",
            boxShadow: "0",
          }}
        >
          <TableContainer>
            <p id="subheader" style={{ padding: "20px" }}>
              Latest Ticket Sales
            </p>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{
                        top: 0,
                        minWidth: column.minWidth,
                        fontFamily: "AuthenticSans90",
                        color: COLORS.grey,
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {latestTickets.length === 0 && (
                  <p style={{ padding: "20px" }}>No ticket sales yet</p>
                )}
                {latestTickets.map((ticket, index) => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                      {columns.map((column) => {
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                              padding: "10px",
                              fontFamily: "AuthenticSans90",
                              fontWeight: "bold",
                            }}
                          >
                            {column.id === "image" && (
                              <img
                                src={userData.logo}
                                style={{ width: "30px" }}
                              />
                            )}
                            {column.id === "status" && (
                              <Status ticket={ticket} shorten={true} />
                            )}
                            {column.id === "date" && timeAgo(ticket.createdAt)}
                            {column.id === "category" && ticket.category_name}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>
      {event.pwa_images_block && event.pwa_images_block.length > 0 && (
        <Paper
          sx={{
            height: "fit-content",
            border: "1px solid #d8d8d8",
            boxShadow: "0",
            marginTop: "20px",
          }}
        >
          <TableContainer>
            <p id="subheader" style={{ padding: "20px" }}>
              LiveSync Ticket - Image Statistics
            </p>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    style={{
                      top: 0,
                      minWidth: "100px",
                      fontFamily: "AuthenticSans90",
                      color: COLORS.grey,
                    }}
                  >
                    Image
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      top: 0,
                      minWidth: "100px",
                      fontFamily: "AuthenticSans90",
                      color: COLORS.grey,
                    }}
                  >
                    Clicks
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      top: 0,
                      minWidth: "100px",
                      fontFamily: "AuthenticSans90",
                      color: COLORS.grey,
                    }}
                  >
                    URL
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {event.pwa_images_block.length === 0 && (
                  <p style={{ padding: "20px" }}>No PWA images yet</p>
                )}
                {event.pwa_images_block.map((image, index) => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "10px",
                          fontFamily: "AuthenticSans90",
                          fontWeight: "bold",
                        }}
                      >
                        <img src={image.url} style={{ height: "20px" }} />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "10px",
                          fontFamily: "AuthenticSans90",
                          fontWeight: "bold",
                        }}
                      >
                        {image.clicks}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "10px",
                          fontFamily: "AuthenticSans90",
                          fontWeight: "bold",
                        }}
                      >
                        {image.link}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <div style={{ height: "50px" }} />
    </div>
  );
};
